import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Youtube, Save, ListMusic, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { ADMIN } from "@/constants/testIds";

const AdminTV = () => {
  const [config, setConfig] = useState(null);
  const [videos, setVideos] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showPlaylistForm, setShowPlaylistForm] = useState(false);
  const [videoForm, setVideoForm] = useState({
    program: "",
    title: "",
    date: new Date().toISOString().slice(0, 10),
    thumbnail_url: "",
    category: "Reportagem",
    description: "",
    source_type: "youtube",
    youtube_id: "",
    video_url: "",
  });
  const [playlistForm, setPlaylistForm] = useState({ name: "", video_ids: [], loop: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, v, p] = await Promise.all([
      adminApi.get("/admin/tv/config"),
      adminApi.get("/admin/videos"),
      adminApi.get("/admin/playlists"),
    ]);
    setConfig(c.data);
    setVideos(v.data);
    setPlaylists(p.data);
  };

  useEffect(() => {
    load();
  }, []);

  const saveConfig = async () => {
    setBusy(true);
    try {
      await adminApi.post("/admin/tv/config", config);
      toast.success("Configuração salva.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveVideo = async (e) => {
    e.preventDefault();
    if (!videoForm.thumbnail_url) {
      toast.error("Envie um arquivo ou cole a URL da thumbnail.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/admin/videos", videoForm);
      toast.success("Vídeo adicionado.");
      setVideoForm({ ...videoForm, title: "", youtube_id: "", video_url: "", thumbnail_url: "" });
      setShowVideoForm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm("Excluir este vídeo permanentemente? Ele será removido do app e não pode ser recuperado.")) return;
    try {
      await adminApi.delete(`/admin/videos/${id}`);
      toast.success("Vídeo excluído permanentemente.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const savePlaylist = async (e) => {
    e.preventDefault();
    if (!playlistForm.name || playlistForm.video_ids.length === 0) {
      toast.error("Informe nome e selecione ao menos 1 vídeo.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/admin/playlists", playlistForm);
      toast.success("Playlist criada.");
      setPlaylistForm({ name: "", video_ids: [], loop: true });
      setShowPlaylistForm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const togglePlVideo = (id) => {
    setPlaylistForm((f) => ({
      ...f,
      video_ids: f.video_ids.includes(id) ? f.video_ids.filter((v) => v !== id) : [...f.video_ids, id],
    }));
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Carregando…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">
          Transmissão
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-tight">
          TV & Vídeos
        </h1>
      </div>

      {/* TV live config */}
      <section className="rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)]">
        <div className="flex items-center gap-2 mb-3">
          <Youtube className="w-5 h-5 text-red-500" />
          <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)]">
            TV Ao Vivo
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Labeled label="Nome do programa">
            <input data-testid={ADMIN.tv.programName} value={config.program_name} onChange={(e) => setConfig({ ...config, program_name: e.target.value })} className="input" />
          </Labeled>
          <Labeled label="Descrição">
            <input data-testid={ADMIN.tv.description} value={config.description} onChange={(e) => setConfig({ ...config, description: e.target.value })} className="input" />
          </Labeled>
          <Labeled label="Fonte do vídeo">
            <select data-testid={ADMIN.tv.sourceType} value={config.source_type} onChange={(e) => setConfig({ ...config, source_type: e.target.value })} className="input bg-white">
              <option value="hls">HLS / m3u8 direto</option>
              <option value="youtube">YouTube (embed)</option>
            </select>
          </Labeled>
          <Labeled label="Capa do programa">
            {config.cover_url && (
              <div className="relative inline-block mb-2">
                <img src={config.cover_url} alt="Capa" className="h-24 w-40 rounded-lg object-cover border border-gray-200" />
                <button
                  type="button"
                  onClick={() => setConfig({ ...config, cover_url: "" })}
                  data-testid="admin-tv-cover-remove"
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white shadow border border-gray-200 text-gray-500 hover:text-[var(--fi-red)] flex items-center justify-center"
                  aria-label="Remover capa"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <label
                data-testid="admin-tv-cover-file"
                className="flex-1 flex items-center gap-2 rounded-xl border border-dashed border-gray-300 hover:border-[var(--fi-blue)] px-3 py-2.5 cursor-pointer transition-colors"
              >
                <ImageIcon className="w-5 h-5 text-[var(--fi-blue)]" />
                <span className="text-sm text-gray-600">
                  {config.cover_url && config.cover_url.startsWith("data:") ? "Trocar arquivo" : "Enviar arquivo JPG ou PNG"}
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
                      toast.error("Envie apenas JPG, PNG ou WEBP.");
                      return;
                    }
                    compressImage(f).catch(() => f).then((img) => {
                      if (img.size > 1.5 * 1024 * 1024) { toast.error("Imagem muito grande mesmo após compressão (máx 1,5 MB)."); return; }
                      const reader = new FileReader();
                      reader.onload = () => setConfig((c) => ({ ...c, cover_url: String(reader.result || "") }));
                      reader.readAsDataURL(img);
                    });
                  }}
                  className="hidden"
                />
              </label>
              <input
                value={config.cover_url && config.cover_url.startsWith("data:") ? "" : config.cover_url}
                onChange={(e) => setConfig({ ...config, cover_url: e.target.value })}
                className="input flex-1"
                placeholder="…ou cole uma URL de imagem"
              />
            </div>
          </Labeled>
          {config.source_type === "hls" ? (
            <Labeled label="URL do stream HLS" className="sm:col-span-2">
              <input data-testid={ADMIN.tv.streamUrl} value={config.stream_url} onChange={(e) => setConfig({ ...config, stream_url: e.target.value })} className="input" placeholder="https://.../playlist.m3u8" />
            </Labeled>
          ) : (
            <Labeled label="ID do vídeo do YouTube (o que vem depois de watch?v=)" className="sm:col-span-2">
              <input data-testid={ADMIN.tv.youtubeId} value={config.youtube_id || ""} onChange={(e) => setConfig({ ...config, youtube_id: e.target.value })} className="input" placeholder="Ex: dQw4w9WgXcQ (funciona com live também)" />
            </Labeled>
          )}
          <Labeled label="Canal do YouTube — sincronização automática (opcional)" className="sm:col-span-2">
            <input
              data-testid="admin-tv-channel-handle"
              value={config.youtube_channel_handle || ""}
              onChange={(e) => setConfig({ ...config, youtube_channel_handle: e.target.value })}
              className="input"
              placeholder="Ex: @falaitapira (ou cole a URL do canal)"
            />
            <div className="text-xs text-gray-500 mt-1">
              Ao informar o canal, a TV pública puxa <b>automaticamente os 15 vídeos mais recentes</b>, atualiza sozinha quando você publica no YouTube e toca em sequência. Deixe em branco para desativar.
              {config.channel_videos && config.channel_videos.length > 0 && (
                <span className="block mt-1 text-emerald-600 font-semibold">
                  ✓ {config.channel_videos.length} vídeos sincronizados agora do canal.
                </span>
              )}
            </div>
          </Labeled>
          <Labeled label="Playlist em loop (opcional — quando termina o vídeo atual, avança para o próximo)" className="sm:col-span-2">
            <select
              data-testid="admin-tv-playlist-select"
              value={config.playlist_id || ""}
              onChange={(e) => setConfig({ ...config, playlist_id: e.target.value || null })}
              className="input bg-white"
            >
              <option value="">Nenhuma — só o vídeo definido acima</option>
              {playlists.map((pl) => (
                <option key={pl.id} value={pl.id}>
                  {`${pl.name} (${pl.video_ids.length} ${pl.video_ids.length !== 1 ? "vídeos" : "vídeo"})`}
                </option>
              ))}
            </select>
            <div className="text-xs text-gray-500 mt-1">
              Só é usada se o canal do YouTube acima estiver vazio. Playlist do canal tem prioridade.
            </div>
          </Labeled>
        </div>
        <button data-testid={ADMIN.tv.saveBtn} disabled={busy} onClick={saveConfig} className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs transition-colors disabled:opacity-60">
          <Save className="w-4 h-4" /> Salvar
        </button>
      </section>

      {/* Videos library */}
      <section className="rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)]">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)]">
            Biblioteca de vídeos
          </h2>
          <button data-testid={ADMIN.tv.newVideoBtn} onClick={() => setShowVideoForm((s) => !s)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--fi-blue)] hover:bg-[var(--fi-blue-2)] text-white text-xs uppercase tracking-widest font-bold">
            <Plus className="w-4 h-4" /> Novo
          </button>
        </div>

        {showVideoForm && (
          <form onSubmit={saveVideo} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
            <Labeled label="Programa"><input value={videoForm.program} onChange={(e) => setVideoForm({ ...videoForm, program: e.target.value })} className="input" required /></Labeled>
            <Labeled label="Título"><input value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} className="input" required /></Labeled>
            <Labeled label="Categoria"><input value={videoForm.category} onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })} className="input" required /></Labeled>
            <Labeled label="Data"><input type="date" value={videoForm.date} onChange={(e) => setVideoForm({ ...videoForm, date: e.target.value })} className="input" required /></Labeled>
            <Labeled label="Fonte">
              <select value={videoForm.source_type} onChange={(e) => setVideoForm({ ...videoForm, source_type: e.target.value })} className="input bg-white">
                <option value="youtube">YouTube</option>
                <option value="hls">HLS / MP4</option>
              </select>
            </Labeled>
            {videoForm.source_type === "youtube" ? (
              <Labeled label="ID do vídeo YouTube"><input value={videoForm.youtube_id} onChange={(e) => setVideoForm({ ...videoForm, youtube_id: e.target.value })} className="input" placeholder="dQw4w9WgXcQ" required /></Labeled>
            ) : (
              <Labeled label="URL do vídeo"><input value={videoForm.video_url} onChange={(e) => setVideoForm({ ...videoForm, video_url: e.target.value })} className="input" placeholder="https://.../vid.mp4" required /></Labeled>
            )}
            <Labeled label="Thumbnail do vídeo" className="sm:col-span-2">
              {videoForm.thumbnail_url && (
                <div className="relative inline-block mb-2">
                  <img src={videoForm.thumbnail_url} alt="Thumb" className="h-20 w-32 rounded-lg object-cover border border-gray-200" />
                  <button
                    type="button"
                    onClick={() => setVideoForm({ ...videoForm, thumbnail_url: "" })}
                    data-testid="admin-tv-thumb-remove"
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white shadow border border-gray-200 text-gray-500 hover:text-[var(--fi-red)] flex items-center justify-center"
                    aria-label="Remover thumbnail"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <label
                  data-testid="admin-tv-thumb-file"
                  className="flex-1 flex items-center gap-2 rounded-xl border border-dashed border-gray-300 hover:border-[var(--fi-blue)] px-3 py-2.5 cursor-pointer transition-colors"
                >
                  <ImageIcon className="w-5 h-5 text-[var(--fi-blue)]" />
                  <span className="text-sm text-gray-600">
                    {videoForm.thumbnail_url && videoForm.thumbnail_url.startsWith("data:") ? "Trocar arquivo" : "Enviar JPG ou PNG"}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
                        toast.error("Envie apenas JPG, PNG ou WEBP.");
                        return;
                      }
                      compressImage(f).catch(() => f).then((img) => {
                        if (img.size > 1.5 * 1024 * 1024) { toast.error("Imagem muito grande mesmo após compressão (máx 1,5 MB)."); return; }
                        const reader = new FileReader();
                        reader.onload = () => setVideoForm((s) => ({ ...s, thumbnail_url: String(reader.result || "") }));
                        reader.readAsDataURL(img);
                      });
                    }}
                    className="hidden"
                  />
                </label>
                <input
                  value={videoForm.thumbnail_url && videoForm.thumbnail_url.startsWith("data:") ? "" : videoForm.thumbnail_url}
                  onChange={(e) => setVideoForm({ ...videoForm, thumbnail_url: e.target.value })}
                  className="input flex-1"
                  placeholder="…ou cole a URL (ex: https://img.youtube.com/vi/ID/maxresdefault.jpg)"
                />
              </div>
            </Labeled>
            <div className="sm:col-span-2">
              <button disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--fi-red)] text-white text-xs font-bold uppercase tracking-widest disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Adicionar vídeo"}
              </button>
            </div>
          </form>
        )}

        <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="admin-tv-videos-list">
          {videos.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              Nenhum vídeo cadastrado. Clique em <b>Novo</b> para adicionar.
            </div>
          )}
          {videos.map((v) => (
            <div key={v.id} data-testid={`admin-tv-video-${v.id}`} className="flex items-center gap-3 p-2 border border-gray-100 rounded-lg">
              <img src={v.thumbnail_url} alt="" className="w-16 h-12 object-cover rounded" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[var(--fi-red)] font-bold">{v.program}</div>
                <div className="text-sm font-bold text-[var(--fi-blue)] truncate">{v.title}</div>
                <div className="text-xs text-gray-500">
                  {v.source_type === "youtube" ? `YouTube: ${v.youtube_id || "-"}` : "HLS/MP4"} · {v.category} · {v.views || 0} views
                </div>
              </div>
              <button
                onClick={() => deleteVideo(v.id)}
                data-testid={`admin-tv-video-delete-${v.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-[var(--fi-red)] border border-[var(--fi-red)]/30 hover:bg-[var(--fi-red)] hover:text-white transition-colors text-xs font-bold uppercase tracking-widest"
                aria-label="Excluir vídeo permanentemente"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Excluir
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Playlists */}
      <section className="rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ListMusic className="w-5 h-5 text-[var(--fi-blue)]" />
            <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)]">
              Playlists em loop
            </h2>
          </div>
          <button data-testid={ADMIN.tv.playlistNewBtn} onClick={() => setShowPlaylistForm((s) => !s)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--fi-blue)] hover:bg-[var(--fi-blue-2)] text-white text-xs uppercase tracking-widest font-bold">
            <Plus className="w-4 h-4" /> Nova
          </button>
        </div>

        {showPlaylistForm && (
          <form onSubmit={savePlaylist} className="p-4 bg-gray-50 rounded-lg space-y-3 mb-4">
            <Labeled label="Nome da playlist">
              <input value={playlistForm.name} onChange={(e) => setPlaylistForm({ ...playlistForm, name: e.target.value })} className="input" placeholder="Ex: Reprises do dia" />
            </Labeled>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">Vídeos ({playlistForm.video_ids.length} selecionados)</div>
              <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                {videos.map((v) => (
                  <label key={v.id} className="flex items-center gap-2 px-3 py-2 hover:bg-white cursor-pointer">
                    <input type="checkbox" checked={playlistForm.video_ids.includes(v.id)} onChange={() => togglePlVideo(v.id)} />
                    <img src={v.thumbnail_url} alt="" className="w-10 h-7 object-cover rounded" />
                    <span className="text-sm text-[var(--fi-blue)] truncate">{v.title}</span>
                  </label>
                ))}
              </div>
            </div>
            <button disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--fi-red)] text-white text-xs font-bold uppercase tracking-widest disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar playlist"}
            </button>
          </form>
        )}

        <div className="space-y-2">
          {playlists.length === 0 && <div className="text-sm text-gray-400 italic">Nenhuma playlist ainda.</div>}
          {playlists.map((pl) => (
            <div key={pl.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
              <div>
                <div className="font-bold text-[var(--fi-blue)]">{pl.name}</div>
                <div className="text-xs text-gray-500">{pl.video_ids.length} vídeo(s) · Loop: {pl.loop ? "Sim" : "Não"}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <style>{`.input{width:100%;padding:10px 12px;border:1px solid #e5e7eb;border-radius:10px;font-size:14px;outline:none;transition:border-color .15s}.input:focus{border-color:#0D1B4B}`}</style>
    </div>
  );
};

const Labeled = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">{label}</label>
    {children}
  </div>
);

export default AdminTV;
